// A local ride can only drop inside the service area. When a rider picks a drop outside it,
// they can book the same trip as an Intertown trip instead. These helpers decide whether
// that will really work (so the button never leads to an error screen) and build the
// hand-over the Intertown page already understands: the same one "Book this route again"
// uses, with a custom destination.
import { customMaxDistanceKm, intertownPickupLimit } from './intertownBooking';
import { tidyPlace } from './intertownDisplay';
import { withTimeout } from './intertownWait';

// The server's own fallbacks (firebase/functions/intertown.js): the road distance it estimates
// when no route is supplied, and the custom-trip limits it applies when the admin set none.
const ROAD_FACTOR = 1.35;
const DEFAULT_MIN_KM = 5;
const DEFAULT_MAX_KM = 120;

// Naming the drop is a nicety: never hold the rider up for it.
export const NAME_LOOKUP_TIMEOUT_MS = 4000;

const num = (value) => (value === null || value === undefined || value === '' ? NaN : Number(value));
const isPoint = (place) => Number.isFinite(num(place?.lat)) && Number.isFinite(num(place?.lng));

const haversineKm = (a, b) => {
  const rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(num(b.lat) - num(a.lat));
  const dLng = rad(num(b.lng) - num(a.lng));
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(rad(num(a.lat))) * Math.cos(rad(num(b.lat))) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
};

// Only what Intertown needs, with a readable name (no plus codes, no trailing "India").
const plainPlace = (place) => tidyPlace({
  name: String(place.name || place.address || ''),
  address: String(place.address || place.name || ''),
  lat: num(place.lat),
  lng: num(place.lng),
});

/**
 * Can this out-of-area drop be booked as an Intertown trip right now? Mirrors what the server
 * checks for a custom trip: custom destinations and a trip type switched on, the pickup inside
 * Intertown's pickup radius, and the road distance (estimated here as the server does) inside
 * the admin's minimum and maximum. When the answer is yes, `state` is the navigation state for
 * `/intertown`. When no, `reason` says why, and the rider simply sees the usual
 * "Outside service area" message.
 */
export function intertownDropRedirect({ platform, pickup, drop } = {}) {
  const config = platform?.intertown;
  if (!config || config.enabled !== true) return { ok: false, reason: 'intertown_off' };
  if (config.custom?.enabled !== true) return { ok: false, reason: 'custom_off' };
  const oneWay = config.oneWayEnabled !== false;
  const roundTrip = config.returnEnabled !== false;
  if (!oneWay && !roundTrip) return { ok: false, reason: 'trip_types_off' };
  if (!isPoint(pickup)) return { ok: false, reason: 'no_pickup' };
  if (!isPoint(drop)) return { ok: false, reason: 'no_drop' };

  // Same centre and radius the server uses; unknown (not set explicitly) means leave it to the server.
  const limit = intertownPickupLimit(platform);
  if (limit && haversineKm(limit.center, pickup) > limit.radiusKm) return { ok: false, reason: 'pickup_outside_intertown' };

  const roadKm = haversineKm(pickup, drop) * ROAD_FACTOR;
  const configuredMin = num(config.custom.minDistanceKm);
  const minKm = Number.isFinite(configuredMin) && configuredMin > 0 ? configuredMin : DEFAULT_MIN_KM;
  const maxKm = customMaxDistanceKm(config.custom) ?? DEFAULT_MAX_KM;
  if (roadKm < minKm) return { ok: false, reason: 'too_close' };
  if (roadKm > maxKm) return { ok: false, reason: 'too_far' };

  const tripType = oneWay ? 'one_way' : 'return';
  return {
    ok: true,
    tripType,
    state: {
      rebookIntertown: { kind: 'custom', tripType, pickup: plainPlace(pickup), destination: plainPlace(drop) },
    },
  };
}

/**
 * Could an out-of-area drop be booked as an Intertown trip from this pickup, whatever the drop
 * turns out to be? Exactly the conditions intertownDropRedirect checks that do not depend on the
 * drop: Intertown on, custom destinations on, a trip type on, and the pickup inside Intertown's
 * pickup radius. Book uses it to let the drop search find towns beyond the local circle: a rider
 * who types "Tirupati" should see Tirupati, and one who cannot book it as Intertown should not be
 * shown places that only lead to an "Outside service area" error.
 */
export function intertownCanTakeOutsideDrop({ platform, pickup } = {}) {
  const config = platform?.intertown;
  if (!config || config.enabled !== true) return false;
  if (config.custom?.enabled !== true) return false;
  if (config.oneWayEnabled === false && config.returnEnabled === false) return false;
  if (!isPoint(pickup)) return false;
  const limit = intertownPickupLimit(platform);
  return !(limit && haversineKm(limit.center, pickup) > limit.radiusKm);
}

/**
 * The navigation state for `/intertown` for a drop pin, or null if it can no longer be booked
 * that way. The pin's own coordinates are kept; only its name and address come from
 * `reverseGeocode`, which is time-limited and may fail: a coordinates label is used then.
 */
export async function intertownHandover({
  platform, pickup, pin, reverseGeocode, fallbackName = '', timeoutMs = NAME_LOOKUP_TIMEOUT_MS,
} = {}) {
  if (!isPoint(pin)) return null;
  const lat = num(pin.lat);
  const lng = num(pin.lng);
  let named = null;
  try {
    named = await withTimeout(Promise.resolve(reverseGeocode(lat, lng)), timeoutMs, 'name');
  } catch {
    named = null;
  }
  const drop = {
    name: named?.name || fallbackName,
    address: named?.address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
    lat,
    lng,
  };
  const plan = intertownDropRedirect({ platform, pickup, drop });
  return plan.ok ? plan.state : null;
}
